import type { RoomSelectorApiRawResponse } from './types'

import type { QueryStringSerializer } from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import type { FilterSelection } from '@/scopes/prices/domain/filters/types'
import type { RoomSelectorResponse } from '@/scopes/prices/domain/rooms/types'
import defaultFetch from 'isomorphic-unfetch'
import { createBrowserQueryString } from '@/scopes/common/infrastructure/query-strings/browserQueryString'
import { BrowserQueryString } from '@/scopes/common/infrastructure/query-strings/types/browserQueryString'
import filterQueryStringSerializer
  from '@/scopes/prices/infrastructure/filter-query-string-serializer/filterQueryStringSerializer'
import {
  createRoomSelectorResponseMapper,
  type RoomSelectorResponseMapper,
} from '@/scopes/prices/infrastructure/rooms/roomSelectorResponseMapper'
import { pricesPassThroughParams } from '@/scopes/prices/types'
import { getPassThroughParams } from '@/scopes/search/search/domain/getPassThroughParams'

interface RoomSelectorContext {
  accoId: string
  contextItemId: string
  bookingGateId: string
}

interface RoomSelectorExtraFilters {
  duration: string
  departureDate: string
}

interface RoomSelectorFilters {
  selectedPriceId: string
}

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
  queryStringSerializer: QueryStringSerializer<RoomSelectorContext & FilterSelection & RoomSelectorExtraFilters>
  roomSelectorResponseMapper: RoomSelectorResponseMapper
  browserQuery: BrowserQueryString
}

export interface RoomSelectorApiClient {
  getRooms: (
    criteria: RoomSelectorContext & { filterSelection: FilterSelection } & RoomSelectorFilters
  ) => Promise<RoomSelectorResponse>
}

export function createRoomSelectorApiClient(dependencies: Partial<Dependencies> = {}) {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    queryStringSerializer = filterQueryStringSerializer,
    roomSelectorResponseMapper = createRoomSelectorResponseMapper(),
    browserQuery = createBrowserQueryString(),
  } = dependencies as Dependencies

  const fetchOptions: RequestInit = {
    referrerPolicy: 'no-referrer-when-downgrade',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  }

  async function getRooms(criteria: RoomSelectorContext & { filterSelection: FilterSelection } & RoomSelectorFilters) {
    const url = buildAPIUrl(criteria)

    const response = await fetch(url, fetchOptions)

    const data = (await response.json()) as RoomSelectorApiRawResponse

    return roomSelectorResponseMapper.map(data)
  }

  function buildAPIUrl({
    accoId,
    contextItemId,
    bookingGateId,
    filterSelection,
    selectedPriceId,
  }: RoomSelectorContext & { filterSelection: FilterSelection } & RoomSelectorFilters) {
    const roomSelectorContext = {
      accoId,
      bookingGateId,
      contextItemId,
    }

    const queryParams = new URLSearchParams(browserQuery.get()!)
    const passThroughParams = getPassThroughParams(queryParams, pricesPassThroughParams)

    const duration = selectedPriceId?.split('|')[0]
    const departureDate = selectedPriceId?.split('|')[1]

    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingGate/GetRoomSelectorApi?${queryStringSerializer.serialize({
      ...roomSelectorContext,
      ...filterSelection,
      departureDate,
      duration,
      ...passThroughParams,
    })}`
  }
  return { getRooms }
}
